/**
 * Active-alarm chip descriptors for the Alarm & Maintenance report grid.
 *
 * Maintenance-report rows carry `active_alarms` as bare alarm display-name
 * strings (no severity or category). This turns each string into an
 * AlarmChipCell descriptor, coloring the chip by the alarm definition's
 * severity — the same status-family vocabulary AlarmChip speaks across the app
 * (site grid, alarm history, map popovers) — and translating the label to the
 * tenant's alarm vocabulary when the definition is known.
 *
 * Contains:
 * - buildActiveAlarmChips — (names, defByDisplayName, translate?) → chip descriptors
 *
 * Returns plain JS — no JSX.
 */
import { severityStatusFamily } from '@sophocles/utils/alarmSeverity';

/**
 * @param {string[]} activeAlarms - Row `active_alarms`: alarm display-name strings.
 * @param {Map<string, { id: string|number, severity?: string }>} alarmDefByDisplayName
 *   Alarm definitions keyed by their raw `display_name` (the exact field the
 *   backend emits in `active_alarms`), so a match yields the alarm's severity.
 * @param {(def: object|undefined, name: string) => React.ReactNode} [translate]
 *   Optional label resolver; receives the matched definition (or undefined) and
 *   the raw name. When omitted the raw display-name string is used verbatim.
 * @returns {Array<{ id: string, label: React.ReactNode, color: string }>}
 *   AlarmChipCell descriptors in row order; `color` is a status-palette family.
 */
export function buildActiveAlarmChips(activeAlarms, alarmDefByDisplayName, translate) {
  if (!Array.isArray(activeAlarms)) return [];
  return activeAlarms.map((name, index) => {
    const def = alarmDefByDisplayName.get(name);
    return {
      id: `${def?.id ?? 'unknown'}-${index}`,
      label: translate ? translate(def, name) : name,
      color: severityStatusFamily(def?.severity),
    };
  });
}
